import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import UserLink from "@/components/UserLink";
import FlagButton from "@/components/FlagButton";
import { useHashScroll } from "@/lib/useHashScroll";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Star, Trash2, MessageSquareReply, ThumbsUp, ThumbsDown, ImagePlus, Video, X, Pencil } from "lucide-react";
import { toast } from "sonner";

const MAX_PHOTOS = 4;
const MAX_VIDEO_MB = 50;
const MAX_VIDEO_SECONDS = 62;

// Format an edit timestamp with date + time, in the entity's timezone when known
// (events), otherwise the viewer's local zone — always labeled with the zone.
function formatEditedAt(iso, tz) {
    if (!iso) return "";
    const d = new Date(iso);
    const opts = { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" };
    try {
        return new Intl.DateTimeFormat("en-US", tz ? { ...opts, timeZone: tz } : opts).format(d);
    } catch {
        return new Intl.DateTimeFormat("en-US", opts).format(d);
    }
}

function Stars({ value, size = 14, onChange, accent = "#ff4500" }) {
    const isInteractive = typeof onChange === "function";
    return (
        <div className="inline-flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((n) => (
                <button
                    key={n}
                    type="button"
                    onClick={isInteractive ? () => onChange(n) : undefined}
                    disabled={!isInteractive}
                    data-testid={isInteractive ? `star-${n}` : undefined}
                    aria-label={`${n} stars`}
                    className={`${isInteractive ? "cursor-pointer hover:scale-110 transition-transform" : "cursor-default"}`}
                    tabIndex={isInteractive ? 0 : -1}
                >
                    <Star
                        style={{ width: size, height: size, ...(n <= value ? { fill: "#facc15", color: "#facc15" } : {}) }}
                        className={n <= value ? "" : "text-[#444]"}
                    />
                </button>
            ))}
        </div>
    );
}

// Read a local video file's duration (seconds) without uploading it.
function videoDuration(file) {
    return new Promise((resolve, reject) => {
        const v = document.createElement("video");
        v.preload = "metadata";
        v.onloadedmetadata = () => { const d = v.duration; URL.revokeObjectURL(v.src); resolve(d); };
        v.onerror = () => { URL.revokeObjectURL(v.src); reject(new Error("bad video")); };
        v.src = URL.createObjectURL(file);
    });
}

export default function Reviews({ teamId, entityType = "team", entityId, canRespond = false, accent = "#ff4500" }) {
    const { user } = useAuth();
    const id = entityId || teamId;
    const base = entityType === "event"
        ? `/events/${id}`
        : entityType === "trainer_listing"
            ? `/trainer-listings/${id}`
            : `/teams/${id}`;
    const noun = entityType === "event" ? "event" : entityType === "trainer_listing" ? "trainer" : "program";
    const respLabel = entityType === "event"
        ? "Response from the host"
        : entityType === "trainer_listing"
            ? "Response from the trainer"
            : "Response from the team";
    const [reviews, setReviews] = useState([]);
    const [avg, setAvg] = useState(0);
    const [count, setCount] = useState(0);
    const [entityTz, setEntityTz] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sort, setSort] = useState("newest");
    const [showForm, setShowForm] = useState(false);
    const [rating, setRating] = useState(5);
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [media, setMedia] = useState([]);
    const [mediaBusy, setMediaBusy] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const photoInputRef = useRef(null);
    const videoInputRef = useRef(null);
    useHashScroll(!loading);
    const [respondingId, setRespondingId] = useState(null);
    const [responseBody, setResponseBody] = useState("");
    const [responseBusy, setResponseBusy] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get(`${base}/reviews`, { params: { sort } });
            setReviews(data.reviews);
            setAvg(data.average);
            setCount(data.count);
            setEntityTz(data.entity_timezone || null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [base, sort]);

    const myReview = user ? reviews.find((r) => r.user_id === user.id) : null;

    const photoCount = media.filter((m) => m.type === "image").length;
    const hasVideo = media.some((m) => m.type === "video");

    const addPhotos = async (files) => {
        let slots = MAX_PHOTOS - media.filter((m) => m.type === "image").length;
        for (const file of Array.from(files || [])) {
            if (slots <= 0) { toast.error(`Up to ${MAX_PHOTOS} photos`); break; }
            if (!file.type?.startsWith("image/")) { toast.error("Please choose an image file"); continue; }
            if (file.size > 8 * 1024 * 1024) { toast.error("Image too large (max 8 MB)"); continue; }
            setMediaBusy(true);
            try {
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await api.post("/uploads", fd);
                setMedia((m) => [...m, { url: data.url, type: "image" }]);
                slots--;
            } catch (err) {
                toast.error(err?.response?.data?.detail || "Photo upload failed");
            } finally {
                setMediaBusy(false);
            }
        }
    };

    const addVideo = async (file) => {
        if (!file) return;
        if (hasVideo) return toast.error("Only 1 video allowed");
        if (!file.type?.startsWith("video/")) return toast.error("Please choose a video file");
        if (file.size > MAX_VIDEO_MB * 1024 * 1024) return toast.error(`Video too large (max ${MAX_VIDEO_MB} MB)`);
        let dur = null;
        try { dur = await videoDuration(file); } catch { /* metadata unreadable — let server accept by size */ }
        if (dur && dur > MAX_VIDEO_SECONDS) return toast.error("Video must be 60 seconds or less");
        setMediaBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/uploads/video", fd);
            setMedia((m) => [...m, { url: data.url, type: "video" }]);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Video upload failed");
        } finally {
            setMediaBusy(false);
        }
    };

    const removeMedia = (idx) => setMedia((m) => m.filter((_, i) => i !== idx));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!user) return toast.error("Please log in to write a review");
        if (title.trim().length < 2 || body.trim().length < 10) return toast.error("Title and review body are too short");
        setSubmitting(true);
        try {
            await api.post(`${base}/reviews`, { rating, title, body, media });
            toast.success(myReview ? "Review updated" : "Review posted");
            setShowForm(false);
            setTitle("");
            setBody("");
            setRating(5);
            setMedia([]);
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not submit review");
        } finally {
            setSubmitting(false);
        }
    };

    const castVote = async (r, kind) => {
        if (!user) return toast.error("Log in to vote on reviews");
        if (r.user_id === user.id) return;
        const value = r.my_vote === kind ? "clear" : kind;
        // optimistic
        setReviews((list) => list.map((x) => {
            if (x.id !== r.id) return x;
            let a = x.agree_count || 0, d = x.disagree_count || 0;
            if (x.my_vote === "agree") a--; if (x.my_vote === "disagree") d--;
            if (value === "agree") a++; if (value === "disagree") d++;
            return { ...x, agree_count: a, disagree_count: d, my_vote: value === "clear" ? null : value };
        }));
        try {
            const { data } = await api.post(`/reviews/${r.id}/vote`, { value });
            setReviews((list) => list.map((x) => x.id === r.id
                ? { ...x, agree_count: data.agree_count, disagree_count: data.disagree_count, my_vote: data.my_vote }
                : x));
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not record your vote");
            await load();
        }
    };

    const onDelete = async (rid) => {
        try {
            await api.delete(`/reviews/${rid}`);
            toast.success("Review deleted");
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const openResponse = (r) => {
        setRespondingId(r.id);
        setResponseBody(r.owner_response?.body || "");
    };

    const submitResponse = async (rid) => {
        if (responseBody.trim().length < 2) return toast.error("Response is too short");
        setResponseBusy(true);
        try {
            await api.post(`/reviews/${rid}/response`, { body: responseBody.trim() });
            toast.success("Response posted");
            setRespondingId(null);
            setResponseBody("");
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not post response");
        } finally {
            setResponseBusy(false);
        }
    };

    const removeResponse = async (rid) => {
        try {
            await api.delete(`/reviews/${rid}/response`);
            toast.success("Response removed");
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const openEdit = () => {
        if (myReview) {
            setRating(myReview.rating);
            setTitle(myReview.title);
            setBody(myReview.body);
            setMedia(Array.isArray(myReview.media) ? myReview.media : []);
        } else {
            setMedia([]);
        }
        setShowForm(true);
    };

    if (loading) return null;

    const SortBtn = ({ value, label }) => (
        <button
            type="button"
            data-testid={`reviews-sort-${value}`}
            onClick={() => setSort(value)}
            className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 border transition-colors ${sort === value ? "border-current text-white" : "border-[#2a2a2a] text-[#a0a0a0] hover:text-white"}`}
            style={sort === value ? { borderColor: accent, color: "#fff" } : {}}
        >
            {label}
        </button>
    );

    return (
        <section data-testid="reviews-section" className="mt-10">
            <div className="flex items-baseline justify-between">
                <h2 className="font-display text-2xl tracking-wide">Reviews</h2>
                <div className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]" data-testid="reviews-count">
                    {count} {count === 1 ? "review" : "reviews"}
                </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                    <Stars value={Math.round(avg)} size={20} accent={accent} />
                    <span className="font-display text-2xl text-white" data-testid="reviews-average">{avg || "—"}</span>
                </div>
                {user && !showForm && (
                    <Button
                        data-testid="write-review-btn"
                        onClick={openEdit}
                        size="sm"
                        style={{ backgroundColor: accent }}
                        className="rounded-none hover:opacity-90 uppercase tracking-[0.2em] text-xs"
                    >
                        {myReview ? "Edit Your Review" : "Write a Review"}
                    </Button>
                )}
                {!user && (
                    <div className="text-xs text-[#a0a0a0]">Log in to write a review.</div>
                )}
                {count > 1 && (
                    <div className="flex items-center gap-1.5 ml-auto">
                        <span className="text-[10px] uppercase tracking-[0.15em] text-[#666]">Sort</span>
                        <SortBtn value="newest" label="Newest" />
                        <SortBtn value="agreed" label="Most Agreed" />
                    </div>
                )}
            </div>

            {showForm && (
                <form onSubmit={onSubmit} className="mt-6 border border-[#2a2a2a] bg-[#141414] p-5 space-y-3" data-testid="review-form">
                    <div className="flex items-center gap-3">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Rating</span>
                        <Stars value={rating} size={24} onChange={setRating} accent={accent} />
                    </div>
                    <Input
                        data-testid="review-title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Headline (e.g. 'Great development program')"
                        maxLength={120}
                        className="rounded-none bg-[#0a0a0a] border-[#2a2a2a] h-11"
                    />
                    <Textarea
                        data-testid="review-body"
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        placeholder={`Share your experience with this ${noun}…`}
                        rows={4}
                        maxLength={2000}
                        className="rounded-none bg-[#0a0a0a] border-[#2a2a2a]"
                    />

                    {/* Media picker */}
                    <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                            <button
                                type="button"
                                data-testid="review-add-photo-btn"
                                disabled={mediaBusy || photoCount >= MAX_PHOTOS}
                                onClick={() => photoInputRef.current?.click()}
                                className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#2a2a2a] hover:border-[#ff4500] text-xs uppercase tracking-wider transition-colors disabled:opacity-40"
                            >
                                <ImagePlus className="h-3.5 w-3.5" /> Photo ({photoCount}/{MAX_PHOTOS})
                            </button>
                            <button
                                type="button"
                                data-testid="review-add-video-btn"
                                disabled={mediaBusy || hasVideo}
                                onClick={() => videoInputRef.current?.click()}
                                className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#2a2a2a] hover:border-[#ff4500] text-xs uppercase tracking-wider transition-colors disabled:opacity-40"
                            >
                                <Video className="h-3.5 w-3.5" /> Video ({hasVideo ? 1 : 0}/1)
                            </button>
                            {mediaBusy && <span className="text-[10px] uppercase tracking-wider text-[#a0a0a0]">Uploading…</span>}
                            <input ref={photoInputRef} type="file" accept="image/*" multiple className="hidden" data-testid="review-photo-input"
                                onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }} />
                            <input ref={videoInputRef} type="file" accept="video/*" className="hidden" data-testid="review-video-input"
                                onChange={(e) => { addVideo(e.target.files?.[0]); e.target.value = ""; }} />
                        </div>
                        <div className="text-[10px] text-[#666]">Up to {MAX_PHOTOS} photos and 1 video (max {MAX_VIDEO_MB} MB / 60 sec).</div>
                        {media.length > 0 && (
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="review-media-preview">
                                {media.map((m, i) => (
                                    <div key={i} className="relative group border border-[#2a2a2a] bg-black">
                                        {m.type === "video"
                                            ? <video src={m.url} className="w-full h-20 object-cover" muted />
                                            : <img src={m.url} alt="" className="w-full h-20 object-cover" />}
                                        {m.type === "video" && (
                                            <span className="absolute bottom-1 left-1 text-[8px] uppercase tracking-wider bg-black/70 px-1 text-white">Video</span>
                                        )}
                                        <button
                                            type="button"
                                            data-testid={`review-media-remove-${i}`}
                                            onClick={() => removeMedia(i)}
                                            className="absolute top-1 right-1 h-5 w-5 flex items-center justify-center bg-black/70 text-white hover:bg-red-600"
                                            aria-label="Remove"
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button type="button" variant="ghost" onClick={() => { setShowForm(false); setMedia([]); }} className="rounded-none border border-[#2a2a2a] hover:bg-transparent">
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            data-testid="review-submit"
                            disabled={submitting || mediaBusy}
                            style={{ backgroundColor: accent }}
                            className="rounded-none hover:opacity-90 uppercase tracking-[0.2em] text-xs"
                        >
                            {submitting ? "Posting…" : "Post Review"}
                        </Button>
                    </div>
                </form>
            )}

            <div className="mt-6 space-y-3">
                {reviews.length === 0 ? (
                    <div className="border border-[#2a2a2a] p-8 text-center text-[#a0a0a0]" data-testid="reviews-empty">
                        Be the first to review this {noun}.
                    </div>
                ) : reviews.map((r) => (
                    <div key={r.id} id={`review-${r.id}`} data-testid={`review-${r.id}`} className="border border-[#2a2a2a] p-5 bg-[#141414]">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <Stars value={r.rating} size={14} accent={accent} />
                                <div className="font-display text-xl tracking-wide mt-1">{r.title}</div>
                                <div className="text-xs text-[#a0a0a0] mt-0.5">
                                    by {r.user_username ? <UserLink username={r.user_username} prefix={false} /> : (r.user_name || "user")} · {new Date(r.created_at).toLocaleDateString()}
                                </div>
                            </div>
                            {user && r.user_id === user.id && (
                                <div className="flex items-center gap-2 shrink-0">
                                    <button
                                        onClick={() => { openEdit(); document.querySelector('[data-testid=reviews-section]')?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                                        data-testid={`review-edit-${r.id}`}
                                        aria-label="Edit review"
                                        title="Edit your review"
                                        className="text-[#a0a0a0] hover:text-[#ff4500] transition-colors"
                                    >
                                        <Pencil className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => onDelete(r.id)}
                                        data-testid={`review-delete-${r.id}`}
                                        aria-label="Delete review"
                                        title="Delete your review"
                                        className="text-[#a0a0a0] hover:text-[#ff4500] transition-colors"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            )}
                        </div>
                        <p className="mt-3 text-[#cfcfcf] leading-relaxed whitespace-pre-wrap">{r.body}</p>

                        {/* Attached media */}
                        {Array.isArray(r.media) && r.media.length > 0 && (
                            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid={`review-media-${r.id}`}>
                                {r.media.map((m, i) => (
                                    m.type === "video" ? (
                                        <video
                                            key={i}
                                            src={m.url}
                                            controls
                                            playsInline
                                            data-testid={`review-video-${r.id}-${i}`}
                                            className="col-span-2 sm:col-span-3 w-full max-h-80 bg-black border border-[#2a2a2a]"
                                        />
                                    ) : (
                                        <a key={i} href={m.url} target="_blank" rel="noreferrer" className="block group">
                                            <img
                                                src={m.url}
                                                alt=""
                                                data-testid={`review-photo-${r.id}-${i}`}
                                                className="w-full h-28 object-cover border border-[#2a2a2a] group-hover:opacity-90 transition-opacity"
                                            />
                                        </a>
                                    )
                                ))}
                            </div>
                        )}

                        {/* Last-edited note */}
                        {r.updated_at && r.updated_at !== r.created_at && (
                            <div className="mt-2 text-[10px] text-[#666] italic" data-testid={`review-edited-${r.id}`}>
                                Last edited {formatEditedAt(r.updated_at, entityTz)}
                            </div>
                        )}

                        {/* Agree / disagree + report */}
                        <div className="mt-4 flex items-center gap-3">
                            <button
                                type="button"
                                data-testid={`review-agree-${r.id}`}
                                onClick={() => castVote(r, "agree")}
                                disabled={!!user && r.user_id === user.id}
                                title={user && r.user_id === user.id ? "You can't vote on your own review" : "Agree"}
                                className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 border transition-colors disabled:opacity-40 ${r.my_vote === "agree" ? "border-emerald-500 text-emerald-400" : "border-[#2a2a2a] text-[#a0a0a0] hover:text-white"}`}
                            >
                                <ThumbsUp className="h-3.5 w-3.5" /> <span data-testid={`review-agree-count-${r.id}`}>{r.agree_count || 0}</span>
                            </button>
                            <button
                                type="button"
                                data-testid={`review-disagree-${r.id}`}
                                onClick={() => castVote(r, "disagree")}
                                disabled={!!user && r.user_id === user.id}
                                title={user && r.user_id === user.id ? "You can't vote on your own review" : "Disagree"}
                                className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 border transition-colors disabled:opacity-40 ${r.my_vote === "disagree" ? "border-red-500 text-red-400" : "border-[#2a2a2a] text-[#a0a0a0] hover:text-white"}`}
                            >
                                <ThumbsDown className="h-3.5 w-3.5" /> <span data-testid={`review-disagree-count-${r.id}`}>{r.disagree_count || 0}</span>
                            </button>
                            {user && r.user_id !== user.id && (
                                <div className="ml-auto">
                                    <FlagButton targetType="review" targetId={r.id} testid={`review-flag-${r.id}`} />
                                </div>
                            )}
                        </div>

                        {/* Official team response */}
                        {r.owner_response ? (
                            <div className="mt-4 border-l-2 bg-[#0f0f0f] p-4" style={{ borderColor: accent }} data-testid={`review-response-${r.id}`}>
                                <div className="flex items-center justify-between gap-2">
                                    <div className="text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1.5" style={{ color: accent }}>
                                        <MessageSquareReply className="h-3.5 w-3.5" /> {respLabel}
                                    </div>
                                    {canRespond && (
                                        <div className="flex items-center gap-3">
                                            <button
                                                type="button"
                                                data-testid={`review-response-edit-${r.id}`}
                                                onClick={() => openResponse(r)}
                                                className="text-[10px] uppercase tracking-[0.15em] text-[#a0a0a0] hover:text-[#ff4500]"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                data-testid={`review-response-remove-${r.id}`}
                                                onClick={() => removeResponse(r.id)}
                                                className="text-[10px] uppercase tracking-[0.15em] text-[#a0a0a0] hover:text-red-500"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <p className="mt-2 text-sm text-[#cfcfcf] leading-relaxed whitespace-pre-wrap">{r.owner_response.body}</p>
                                <div className="text-[10px] text-[#666] mt-2">— {r.owner_response.author_name}</div>
                            </div>
                        ) : null}

                        {/* Owner respond control */}
                        {canRespond && respondingId === r.id ? (
                            <div className="mt-4 border border-[#2a2a2a] bg-[#0f0f0f] p-4 space-y-3" data-testid={`review-response-form-${r.id}`}>
                                <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: accent }}>Your response</div>
                                <Textarea
                                    data-testid={`review-response-input-${r.id}`}
                                    value={responseBody}
                                    onChange={(e) => setResponseBody(e.target.value)}
                                    placeholder="Thank the reviewer or share your side — this is shown publicly under the review."
                                    rows={3}
                                    maxLength={2000}
                                    className="rounded-none bg-[#0a0a0a] border-[#2a2a2a]"
                                />
                                <div className="flex justify-end gap-2">
                                    <Button type="button" variant="ghost" onClick={() => { setRespondingId(null); setResponseBody(""); }} className="rounded-none border border-[#2a2a2a] hover:bg-transparent text-xs">
                                        Cancel
                                    </Button>
                                    <Button
                                        type="button"
                                        data-testid={`review-response-submit-${r.id}`}
                                        disabled={responseBusy}
                                        onClick={() => submitResponse(r.id)}
                                        style={{ backgroundColor: accent }}
                                        className="rounded-none hover:opacity-90 uppercase tracking-[0.2em] text-xs"
                                    >
                                        {responseBusy ? "Posting…" : "Post Response"}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            canRespond && !r.owner_response && (
                                <button
                                    type="button"
                                    data-testid={`review-respond-btn-${r.id}`}
                                    onClick={() => openResponse(r)}
                                    style={{ color: accent }}
                                    className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] hover:underline"
                                >
                                    <MessageSquareReply className="h-3.5 w-3.5" /> Respond to this review
                                </button>
                            )
                        )}
                    </div>
                ))}
            </div>
        </section>
    );
}
